import React, { memo, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { CapacitorUpdater, LatestVersion } from '@capgo/capacitor-updater';
import OneSignal from 'onesignal-cordova-plugin';
import styled from 'styled-components';
import { ReactComponent as NotificationsIllustration } from 'src/assets/images/illustration_notification.svg';
import { ProgressBar } from 'src/components/general/feedback/progress-bar';
import { AlertModal } from 'src/components/general/modals/alert-modal';
import { FlexColumn } from 'src/components/layout/flex-column';
import { MobileHollowLayout } from 'src/components/layout/hollow-layout';
import { NotFoundPage } from 'src/components/pages/errors/not-found-page';
import { InstagramAuthPage } from 'src/components/pages/instagram-channel/instagram-oauth.page';
import { IndustrySwitch } from 'src/components/routing/authenticated-router/industry-switch';
import { CustomLocationState } from 'src/components/routing/lgg-switch';
import { useNotificationPermission } from 'src/hooks/push-notifications/use-notification-permission';
import { useOnesignalSetUserId } from 'src/hooks/push-notifications/use-onesignal-set-user-id';
import { useDefaultInstitution } from 'src/hooks/use-default-institution';
import { useInstitutionPathPrefix } from 'src/hooks/use-institution-path-prefix';
import { useVisible } from 'src/hooks/use-visible';
import {
  downloadBundle,
  getLatestBundle,
  setBundle,
} from 'src/utils/capacitor/app-updater';
import logger from 'src/utils/capacitor/logger';
import { createSuspensePromise } from 'src/utils/suspense-promise';

const useIndexPath = (): string => {
  const location = useLocation<CustomLocationState>();

  const defaultInstitution = useDefaultInstitution();
  const { prefix: defaultPath } = useInstitutionPathPrefix({
    ...defaultInstitution,
    id: Number(defaultInstitution.id),
  });
  const systemInstitutionId = 0;

  if (Number(defaultInstitution.id) === systemInstitutionId) {
    return `${import.meta.env.VITE_ADMIN_BACKEND_BASE_URL}/system`;
  }

  return defaultPath + location.search;
};

const StyledNotificationsIllustration = styled(NotificationsIllustration)`
  height: 150px;
  margin-top: 30px;
  width: 180px;
`;

export const SHOWED_NOTIFICATION_PERMISSION_MODAL = 'showedNotificationPermissionModal';

const PushNotificationOffModal = () => {
  const { permissionState } = useNotificationPermission();
  const { close, show, visible } = useVisible();
  const isNativePlatform = Capacitor.isNativePlatform();
  const displayedModal = useRef(false);
  const { t } = useTranslation(['notifications']);

  const handleClose = async () => {
    close();
    await Preferences.set({
      key: SHOWED_NOTIFICATION_PERMISSION_MODAL,
      value: 'true',
    });
    displayedModal.current = true;
  };

  useEffect(() => {
    if (!isNativePlatform) {
      return;
    }

    const shouldDisplayModal = async () => {
      const showedNotificationPermissionModal = await Preferences.get({
        key: SHOWED_NOTIFICATION_PERMISSION_MODAL,
      });

      if (
        !showedNotificationPermissionModal.value &&
        isNativePlatform &&
        permissionState &&
        permissionState !== 'granted'
      ) {
        show();
      }
    };

    void shouldDisplayModal();
  }, [isNativePlatform, permissionState, show]);

  if (!isNativePlatform) {
    return null;
  }

  return (
    <AlertModal
      visible={visible}
      onClose={handleClose}
      title={t('notifications:pushNotifications.alertModal.title')}
      message={t('notifications:pushNotifications.alertModal.message')}
      icon={<StyledNotificationsIllustration />}
      button={{
        text: t('notifications:pushNotifications.alertModal.action'),
        onClick: () => {
          void handleClose();
          OneSignal.promptForPushNotificationsWithUserResponse(function (accepted) {
            console.debug('User accepted notifications: ' + accepted);
          });
        },
      }}
    />
  );
};

const useUpdateCapgoBundleImmediately = (bundleInfo?: LatestVersion) => {
  const [step, setStep] = useState<'idle' | 'downloading'>('idle');
  const [percentage, setPercentage] = useState(0);

  useEffect(() => {
    let subscription: PluginListenerHandle | null = null;

    void (async () => {
      // Esta lógica solo aplica para plataformas nativas (iOS/Android)
      // En web no hay sistema de actualizaciones de bundles
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      // Si el servidor no tiene un bundle nuevo disponible para esta versión,
      // no hay nada que actualizar
      if (!bundleInfo) {
        return;
      }

      const isBuiltInVersionBundle =
        (await CapacitorUpdater.current()).bundle.id === 'builtin';

      // Contexto: Cuando el usuario descarga el app desde el store por primera vez,
      // Capacitor retorna bundle.id === 'builtin'
      //
      // Estrategia de actualizaciones:
      // - Primera vez (builtin): Mostrar pantalla de loading con progreso de descarga
      // - Subsecuentes: Descargar automáticamente en background sin interrumpir al usuario
      if (!isBuiltInVersionBundle) {
        return;
      }

      subscription = await CapacitorUpdater.addListener('download', (info: any) => {
        setPercentage(info.percent);
        logger.log(`download was fired ${info.percent}`);
      });

      try {
        const url = bundleInfo.url;

        if (url) {
          setStep('downloading');
          const data = await downloadBundle({ ...bundleInfo, url });

          if (data) {
            await setBundle(data.id);
          }
        }
      } catch (e) {
        logger.error(e.toString());
      } finally {
        setStep('idle');
      }
    })();

    return () => {
      void subscription?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { step, percentage };
};

const StyledLoadingOverlay = styled(FlexColumn)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.white};
  z-index: 10000000;
`;

const LoadingDataTitle = styled.span`
  color: ${({ theme }) => theme.colors.steel};
  font-family: ${({ theme }) => theme.font.medium};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-align: center;
  margin-top: 36%;
`;

const LoadingDataMessage = styled.span`
  color: ${({ theme }) => theme.colors.flint};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-align: center;
  margin-bottom: 20px;
`;

const MainContainer = styled(FlexColumn)`
  padding: 0 40px;
  flex: 1;
`;

export const LoadingDataOverlay = memo<{ percentage: number }>(({ percentage }) => {
  const { t } = useTranslation(['common']);

  return (
    <StyledLoadingOverlay>
      <MobileHollowLayout hideFooter={true}>
        <MainContainer>
          <LoadingDataTitle>{t('common:pleaseWait')}</LoadingDataTitle>
          <LoadingDataMessage>
            <Trans i18nKey="common:updateApp.synchronizingData" values={{ percentage }} />
          </LoadingDataMessage>
          <ProgressBar percentage={percentage} />
        </MainContainer>
      </MobileHollowLayout>
    </StyledLoadingOverlay>
  );
});

const getLatestBundleFetcher = createSuspensePromise(async () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  logger.info('getting latest bundler from suspense');

  try {
    return await getLatestBundle();
  } catch (e) {
    // it's fine
  }
});

export const AuthenticatedRouter = memo(() => {
  const defaultPath = useIndexPath();
  const location = useLocation<CustomLocationState>();
  const effectiveLocation = location.state?.background || location;
  useOnesignalSetUserId();
  const bundle = getLatestBundleFetcher.read();
  const { step, percentage } = useUpdateCapgoBundleImmediately(bundle);

  return (
    <>
      <Switch location={effectiveLocation}>
        {/* https://github.com/remix-run/react-router/issues/4841 */}
        <Redirect
          exact
          strict
          from="(.*//+.*)"
          to={
            window.location.pathname.replace(/\/\/+/g, '/') +
            window.location.search +
            window.location.hash
          }
        />
        {/* {import.meta.env.MODE === 'development' ? (
        <Route path="/gql-test">
          <GqlSubscriptionTest />
        </Route>
      ) : null} */}
        <Route path="/instagram/auth">
          <InstagramAuthPage />
        </Route>
        <Route path="/account/login">
          {/* in case someone has this bookmarked, and they're already logged in */}
          <Redirect to="/" />
        </Route>
        <Route
          path="/"
          exact
          render={() => {
            if (defaultPath?.startsWith('http')) {
              window.location.replace(defaultPath);
            } else {
              return <Redirect to={defaultPath} />;
            }
          }}
        />
        <Route path="/i/:institutionType/:institutionId">
          <IndustrySwitch />
        </Route>
        <Route path="*">
          <NotFoundPage />
        </Route>
      </Switch>
      <PushNotificationOffModal />
      {step === 'downloading' && <LoadingDataOverlay percentage={percentage} />}
    </>
  );
});
